import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, X, Pin } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { storage } from '@/lib/storage';
import { uuid } from '@/lib/utils';
import { cn } from '@/lib/cn';

interface Note {
  id: string;
  text: string;
  color: string;
  pinned: boolean;
  ts: number;
}

const COLORS = [
  'bg-yellow-200/70 border-yellow-300/80 dark:bg-yellow-500/15 dark:border-yellow-500/30',
  'bg-pink-200/70 border-pink-300/80 dark:bg-pink-500/15 dark:border-pink-500/30',
  'bg-blue-200/70 border-blue-300/80 dark:bg-blue-500/15 dark:border-blue-500/30',
  'bg-green-200/70 border-green-300/80 dark:bg-green-500/15 dark:border-green-500/30',
  'bg-purple-200/70 border-purple-300/80 dark:bg-purple-500/15 dark:border-purple-500/30',
  'bg-orange-200/70 border-orange-300/80 dark:bg-orange-500/15 dark:border-orange-500/30',
];

export function StickyNotes() {
  const [notes, setNotes] = useState<Note[]>(() =>
    storage.get<Note[]>('stickyNotes', []),
  );

  useEffect(() => {
    storage.set('stickyNotes', notes);
  }, [notes]);

  function add() {
    const note: Note = {
      id: uuid(),
      text: '',
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      pinned: false,
      ts: Date.now(),
    };
    setNotes((n) => [note, ...n]);
  }

  function remove(id: string) {
    setNotes((n) => n.filter((x) => x.id !== id));
  }

  function update(id: string, patch: Partial<Note>) {
    setNotes((n) => n.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  }

  const sorted = [...notes].sort((a, b) => Number(b.pinned) - Number(a.pinned));

  return (
    <div>
      <ToolHeader
        toolId="sticky-notes"
        title="Sticky notes"
        description="Quick scratch notes saved automatically to local storage."
        actions={
          <Button size="sm" variant="primary" onClick={add}>
            <Plus size={12} />
            New note
          </Button>
        }
      />

      {notes.length === 0 ? (
        <EmptyState
          icon={<Plus size={20} />}
          title="No sticky notes yet"
          description="Notes auto-save and persist between visits."
          action={
            <Button variant="primary" onClick={add}>
              <Plus size={12} />
              Create first note
            </Button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <AnimatePresence>
            {sorted.map((note) => (
              <motion.div
                key={note.id}
                layout
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.92 }}
                transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
                className={cn(
                  'relative rounded-2xl border p-4 shadow-sm group',
                  note.color,
                )}
              >
                <div className="flex items-center justify-between mb-2 -mt-1 -mx-1">
                  <button
                    onClick={() => update(note.id, { pinned: !note.pinned })}
                    className={cn(
                      'size-7 rounded-md flex items-center justify-center transition',
                      note.pinned
                        ? 'text-warning'
                        : 'text-fg-subtle opacity-0 group-hover:opacity-100',
                    )}
                  >
                    <Pin size={12} className={note.pinned ? 'fill-warning' : ''} />
                  </button>
                  <button
                    onClick={() => remove(note.id)}
                    className="size-7 rounded-md flex items-center justify-center text-fg-subtle hover:text-danger opacity-0 group-hover:opacity-100 transition"
                  >
                    <X size={12} />
                  </button>
                </div>
                <textarea
                  value={note.text}
                  onChange={(e) => update(note.id, { text: e.target.value })}
                  placeholder="Type a note…"
                  className="w-full bg-transparent border-0 outline-none resize-none text-sm leading-relaxed min-h-[140px] placeholder:text-fg-subtle"
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}

export default StickyNotes;
